import React, { useState } from 'react';
import { ApprovalRequest as ApprovalRequestType } from '../../contexts/ChatContext';

interface ApprovalRequestProps {
  request: ApprovalRequestType;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onAskQuestion: (id: string, question: string) => void;
}

const ApprovalRequest: React.FC<ApprovalRequestProps> = ({
  request,
  onApprove,
  onReject,
  onAskQuestion,
}) => {
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [question, setQuestion] = useState('');

  const getRiskStyles = (level: 'low' | 'medium' | 'high') => {
    switch (level) {
      case 'low':
        return { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', label: 'Low Risk' };
      case 'medium':
        return { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: 'Medium Risk' };
      case 'high':
        return { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', label: 'High Risk' };
    }
  };

  const getTypeIcon = (type: ApprovalRequestType['type']) => {
    switch (type) {
      case 'deployment':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        );
      case 'config_change':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4" />
          </svg>
        );
      case 'user_action':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        );
      case 'code_execution':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        );
    }
  };

  const riskStyles = getRiskStyles(request.riskLevel);

  if (request.status !== 'pending') {
    return (
      <div
        style={{
          padding: '0.75rem 1rem',
          background: request.status === 'approved' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          borderRadius: '8px',
          fontSize: '0.875rem',
          color: request.status === 'approved' ? '#10b981' : '#ef4444',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        {request.status === 'approved' ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        )}
        {request.summary} - {request.status}
      </div>
    );
  }

  return (
    <div
      className="approval-request"
      style={{
        background: 'var(--bg-secondary)',
        border: '2px solid #f59e0b',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1rem',
      }}
    >
      {/* Header */}
      <div
        className="approval-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '0.75rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(245, 158, 11, 0.1)',
            color: '#f59e0b',
            flexShrink: 0,
          }}
        >
          {getTypeIcon(request.type)}
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.25rem',
            }}
          >
            <span
              style={{
                color: '#f59e0b',
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Approval Needed
            </span>
            <span
              style={{
                fontSize: '0.7rem',
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                background: riskStyles.bg,
                color: riskStyles.color,
              }}
            >
              {riskStyles.label}
            </span>
          </div>
          <h4 style={{ margin: 0, fontWeight: 500, fontSize: '0.95rem' }}>
            {request.summary}
          </h4>
        </div>
      </div>

      {/* Details */}
      <div
        className="approval-details"
        style={{
          background: 'var(--bg-tertiary)',
          borderRadius: '8px',
          padding: '0.75rem',
          marginBottom: '1rem',
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
          whiteSpace: 'pre-wrap',
        }}
      >
        {request.details}
      </div>

      {/* Reject reason input */}
      {showRejectReason && (
        <div style={{ marginBottom: '1rem' }}>
          <textarea
            placeholder="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              resize: 'vertical',
              minHeight: '60px',
              fontSize: '0.875rem',
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button
              onClick={() => {
                onReject(request.id, rejectReason);
                setShowRejectReason(false);
              }}
              style={{
                flex: 1,
                padding: '0.5rem',
                borderRadius: '6px',
                border: 'none',
                background: '#ef4444',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Confirm Reject
            </button>
            <button
              onClick={() => setShowRejectReason(false)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: '1px solid var(--border-color)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Question input */}
      {showQuestion && (
        <div style={{ marginBottom: '1rem' }}>
          <textarea
            placeholder="Ask a question about this request..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              resize: 'vertical',
              minHeight: '60px',
              fontSize: '0.875rem',
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button
              onClick={() => {
                if (question.trim()) {
                  onAskQuestion(request.id, question);
                  setQuestion('');
                  setShowQuestion(false);
                }
              }}
              style={{
                flex: 1,
                padding: '0.5rem',
                borderRadius: '6px',
                border: 'none',
                background: 'var(--primary)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Send Question
            </button>
            <button
              onClick={() => setShowQuestion(false)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: '1px solid var(--border-color)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!showRejectReason && !showQuestion && (
        <div
          className="approval-actions"
          style={{
            display: 'flex',
            gap: '0.5rem',
          }}
        >
          <button
            onClick={() => onApprove(request.id)}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: '#10b981',
              color: 'white',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Approve
          </button>
          <button
            onClick={() => setShowRejectReason(true)}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: '#ef4444',
              color: 'white',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Reject
          </button>
          <button
            onClick={() => setShowQuestion(true)}
            style={{
              padding: '0.625rem 1rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'transparent',
              color: 'var(--text-primary)',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Ask
          </button>
        </div>
      )}
    </div>
  );
};

export default ApprovalRequest;
